import { Record, Map as ImmutableMap } from 'immutable';
import getIn from 'immutable-less/transmute/getIn';
import { expressionToList } from 'automation-ui-formula-editor/formula-editor/grammar/expressionUtils';
import ExpressionTypes from 'automation-ui-formula-editor/formula-editor/grammar/constants/ExpressionTypes';
import { OBJECT_PROPERTY, FIELD_DATA, FETCHED_OBJECT_PROPERTY } from 'automation-ui-formula-editor/formula-editor/data-passing/constants/DataSourceOptions';
import { getFlattenedProperties } from 'automation-ui-formula-editor/formula-editor/utils/getFlattenedProperties';
import getTokenParts from 'automation-ui-formula-editor/data-sources/utils/getTokenParts';
export default class FormulaState extends Record({
  formulaText: '',
  parsedExpression: {},
  variableContext: ImmutableMap(),
  validationErrors: [],
  syntaxErrors: []
}, 'FormulaState') {
  cleanVariableContext() {
    if (!this.syntaxErrors.length) {
      const fieldExpressions = this.getFieldExpressions();
      const newContext = this.variableContext.filter((value, key) => !!fieldExpressions.find(expression => expression.name === key));
      return this.set('variableContext', newContext);
    }
    return this;
  }
  hasValidationErrors() {
    return !!this.validationErrors.length;
  }
  hasSyntaxErrors() {
    return !!this.syntaxErrors.length;
  }
  getAllErrors() {
    return [...this.syntaxErrors, ...this.validationErrors];
  }
  getErrorCount() {
    return this.validationErrors.length + this.syntaxErrors.length;
  }
  getFieldExpressions() {
    const expressionList = expressionToList(this.parsedExpression) || [];
    return expressionList.filter(expression => expression.type === ExpressionTypes.FIELD);
  }
  getFieldExpressionObject({
    fieldName,
    actionOutputs,
    propertiesByGroup,
    dataSources
  }) {
    return FormulaState.getFieldExpressionObject({
      variableContext: this.variableContext,
      fieldName,
      actionOutputs,
      propertiesByGroup,
      dataSources
    });
  }
  static getFieldExpressionObject({
    variableContext,
    fieldName,
    actionOutputs,
    propertiesByGroup,
    dataSources
  }) {
    if (!variableContext.has(fieldName) && fieldName) {
      if (dataSources && fieldName && dataSources.get(fieldName)) {
        const propertyData = dataSources.get(fieldName);
        return {
          fieldType: OBJECT_PROPERTY,
          reference: Object.assign({}, propertyData, {
            typeLabel: propertyData.label,
            name: fieldName,
            type: propertyData.metadata.typeDefinition.type
          })
        };
      }
      return null;
    }
    const variableCoordinates = variableContext.get(fieldName);
    const variableType = variableCoordinates && variableCoordinates.get('type');
    if (variableType === OBJECT_PROPERTY && propertiesByGroup) {
      const reference = getFlattenedProperties(propertiesByGroup).find(property => property.name === variableCoordinates.get('propertyName'));
      if (reference && variableType) {
        return {
          fieldType: variableType,
          reference
        };
      }
    } else if (variableType === FIELD_DATA && actionOutputs) {
      const actionId = parseInt(getIn(['identifier', 'actionId'], variableCoordinates), 10);
      const dataKey = variableCoordinates.getIn(['identifier', 'dataKey']);
      const output = actionOutputs.get(actionId);
      if (output && output.outputFieldDefinitions) {
        const fieldDefinitions = output.outputFieldDefinitions;
        const {
          typeDefinition: {
            type,
            name,
            label: typeLabel,
            options
          }
        } = fieldDefinitions.find(definition => definition.typeDefinition.name === dataKey);
        const actionOutput = actionOutputs.get(actionId);
        return {
          fieldType: variableType,
          reference: {
            actionOutput,
            typeLabel: typeLabel || name,
            type: type,
            name: name,
            options
          }
        };
      }
    } else if (variableType === FETCHED_OBJECT_PROPERTY) {
      const {
        tokenPath
      } = getTokenParts(variableCoordinates.get('propertyToken') || '');
      const propertyData = dataSources.get(tokenPath);
      if (propertyData) {
        return {
          fieldType: OBJECT_PROPERTY,
          reference: Object.assign({}, propertyData, {
            typeLabel: propertyData.label,
            name: fieldName,
            type: propertyData.metadata.typeDefinition.type
          })
        };
      }
    }
    return null;
  }
}